<template>
  <div :class="['lore-page']">
    <div :class="['lore']">
      <!-- Hero -->
      <LoreHero />
      <LoreNav />
      
      <div class="lore-center">
        <div :class="['lore-container']">
          
          <!-- Preface -->
           <div class="lore-section" id="preface-section">
            <LoreSectionHeader label="Preface" header="Carry What Matters" />
            <div class="row">
              <div class="col col-3 col-spacer"></div>
              <div class="col col-6">
                <p><span class="laptop-illo"><Laptop /></span>The average computer user creates tens of thousands of files every year. That’s a lot.</p>
                <p>How do you decide what matters, what’s worth holding on to and passing down?</p>
              </div>
            </div>
            <div class="row image-row">
              <div class="col col-2 col-spacer"></div>
              <div class="col col-8">
                <div class="file-to-glowbe">
                  <div class="file-to-glowbe__background">
                    <ImageGlow src="/figures/lore/file-to-glowbe.png" alt="A file becomes a glowbe." />
                  </div>
                  <div class="file-to-glowbe__glowbes">
                    <div class="file-glowbe">
                      <img src="/figures/lore/editorial-glowbe.png" />
                    </div>
                    <div class="file-glowbe">
                      <img src="/figures/lore/editorial-glowbe.png" />
                    </div>
                    <div class="file-glowbe">
                      <img src="/figures/lore/editorial-glowbe.png" />
                    </div>
                    <div class="file-glowbe">
                      <img src="/figures/lore/editorial-glowbe.png" />
                    </div>
                  </div>
                </div>                
              </div>
            </div>
            <div class="row">
              <div class="col col-3 col-spacer"></div>
              <div class="col col-6">
                <p>We decide with a daily discernment. Each day, USB Club members crate dig through their laptop to find a file worth preserving.</p>
                <p class="usbkeys-paragraph">The file gets copied to their USB, <span class="usbkeys-illo"><USBKeys /></span>shared with the network, and their friends can preserve it to their USB as well. An authentic digital selfhood, always close to home.</p>
                <p>That’s USB Club today, but how did it begin?</p>
              </div>
            </div>
          </div>

          <!-- Origin -->
          <div class="lore-section" id="origin-section">
            <LoreSectionHeader label="Origin" header="Bring Your USB" />
            <div class="row">
              <div class="col col-3 col-spacer"></div>
              <div class="col col-6">
                <p><span class="djdeck-illo"><DJDeck /></span>USB Club started with a couple of friends around a DJ deck, learning the craft of mixing. One of a DJ’s most prized possessions is their USB. Curated with tracks and hooked on their keychain, always ready to ignite a room.</p>
                <p>A movement grew out of these DJ sessions, more and more friends had USBs on their keychains. Naturally, we wanted to make it more inclusive for our friends that didn’t DJ, so we threw a potluck party, but for files. “Bring your USB”, we said, and load it with any thing you want to share.</p>
              </div>
            </div>
            <div class="museum-plate">
              <div class="museum-full-width museum-photo-row">
                <ImageGlow src="/figures/lore/dj-roots.jpg" />
                <ImageGlow src="/figures/lore/club-card.jpg" />
                <ImageGlow src="/figures/lore/the-og-transfer.png" />
              </div>
              <div class="museum-inset museum-inset--potluck">
                <div class="museum-inset-title">File Potluck Party</div>
                <div class="museum-inset-subtitle">Brooklyn, NY (2021)</div>
                <div class="museum-inset-body">Bring your USB, share files,<br>sign your usb club card.</div>
              </div>
            </div>
            <div class="row">
              <div class="col col-3 col-spacer"></div>
              <div class="col col-6">
                <p>The night of the potluck about 50 people gathered in a Brooklyn studio. One by one throughout the night people plugged into our laptop and copied their files to a folder. We had a projector up to view some of them, and were amazed that we were seeing sides of our friends we hadn’t seen before. Work-in-progress, screenplays, scanned in paintings, unreleased renders - The USB had provided an intimate medium to share a more authentic, vulnerable and unique expressions.</p>
              </div>
            </div>
            <div class="row image-row image-row--time-place">
              <div class="col col-2 col-spacer"></div>
              <div class="col col-8">
                <TimePlace />
              </div>
            </div>
            <div class="row">
              <div class="col col-3 col-spacer"></div>
              <div class="col col-6">
                <p>By the end of the night, we felt two things:</p>
                <div class="numbered-list">
                  <div class="numbered-list-item">
                    <img class="numbered-list-icon" src="/figures/lore/no-one.svg" />
                    <p>a responsibility to redistribute the files to those that shared in this time and place.</p>
                  </div>
                  <div class="numbered-list-item">
                    <img class="numbered-list-icon numbered-list-icon--2" src="/figures/lore/no-two.svg" />
                    <p>an infrastructure gap to deliver files back to your USB, the medium of origin.</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="row image-row">
              <div class="col col-2 col-spacer"></div>
              <div class="col col-8 col-center">
                <MediumOfOrigin />
              </div>
            </div>
          </div>


          <!-- ATM -->
          <div class="lore-section" id="atm-section">
            <LoreSectionHeader label="ATM" header="People Want to<span class='mobile-show'><br/></span> Feel Again" />
            <div class="row">
              <div class="col col-3 col-spacer"></div>
              <div class="col col-6">
                <p><span class="baggie-illo"><Baggie /></span>Enter Raprave, a local DJ Collective. They had a similar need to fill - distribute their new mixtape to their in-person audience, most of which were musicians and DJs that already had USBs. With this unique market fit identified, we had one week to build the initial ATM.</p>
              </div>
            </div>
            <div class="museum-plate">
              <div class="museum-full-width museum-video-row">
                <div class="row">
                  <div class="col col-9">
                    <VideoGlow src="/figures/lore/ATM-Video.mp4" />
                  </div>
                  <div class="col col-3 museum-inset">
                    <div class="museum-inset-title">The ATM</div>
                    <div class="museum-inset-subtitle">Automatic Transfer Machine</div>
                    <div class="museum-inset-body">An in-person file distribution<br>device for community events.</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row row--align-bottom">
              <div class="col col-3 col-spacer"></div>
              <div class="col col-6">
                <p>Plug-in, get files. This simple idea took us around the globe to help event hosts at Paris Fashion Week, in Tokyo stores, and a festival in California to name a few. The event hosts bought USBs in bulk from us, curated a site-specific set of files for the ATM, and their attendees walked away with digital and physical souvenirs on their keychain. Our global tour with the ATM created a unique distribution channel that didn't exist before.</p>
              </div>
              <div class="col col-3">
                <Quote
                  title="Welcome to the Club"
                  author="Designheads"
                  year="2023"
                  url="https://designheads.substack.com/p/welcome-to-the-club"
                >
                  <template #default>
                    "the internet infrastructure for hardware and a renewed channel for meaningful cultural production and distribution."
                  </template>
                  <template #logo>
                    <svg width="18" height="24" viewBox="0 0 18 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5.78105 7.71269C5.77071 7.72107 5.64469 7.73393 5.53124 7.81273C4.62897 8.43837 3.67752 9.02656 2.78893 9.66282C2.33095 9.99087 2.54639 10.2166 2.48799 10.6595C2.39773 11.3422 2.16581 12.1662 2.08869 12.8556C2.06522 13.0649 2.09623 13.2884 2.09511 13.498C2.33654 13.5209 2.49386 13.7897 2.7378 13.809C3.18628 13.8445 3.76414 13.0805 3.92453 13.1104C4.2604 13.173 4.61947 13.9255 4.83631 14.2005C5.37979 14.8898 5.58378 14.0985 6.13118 13.8042L7.27348 15.2584L7.23213 15.504C7.08878 15.6105 6.43408 16.2288 6.33544 16.1981C6.25301 16.1724 5.4449 15.1628 5.33145 14.9993C5.23002 14.8532 5.32726 14.7029 4.94277 14.8118C4.7807 14.8577 4.39704 15.4696 4.14388 15.3975C3.9413 15.34 3.56603 14.6417 3.38887 14.4505C3.00912 14.0415 2.85851 14.7887 2.70874 15.0731C2.36923 15.7183 2.04202 16.3331 1.73828 16.9973C1.70391 17.0727 1.55554 17.1155 1.59075 17.2426C1.67178 17.5335 2.05264 17.4592 2.22337 17.5125C2.41869 17.5737 2.62854 17.7132 2.85767 17.7783C2.9415 17.802 3.07814 17.7894 3.08708 17.797C3.20081 17.8956 2.70147 18.9208 2.78726 18.9949C3.07451 18.9893 3.364 19.1463 3.63644 19.1941C3.73731 19.2117 3.96309 19.1732 3.98545 19.1944C4.01255 19.2204 4.00054 19.8279 3.98545 19.9435C3.93766 20.316 3.8203 20.5575 3.78566 21.0409C3.77168 21.2362 3.5596 21.3362 3.88401 21.5447C3.97874 21.6056 4.71559 21.405 4.93327 21.4905C5.15513 21.3714 6.27564 21.1884 6.37763 21.294C6.39021 21.3069 6.47655 21.5905 6.48214 21.6388C6.51651 21.9362 6.45671 22.2818 6.47907 22.5867C5.63827 22.7269 4.77595 22.7269 3.94633 22.9826C3.76917 22.9443 3.61213 22.5137 3.58558 22.3396C3.5568 22.1505 3.66299 21.8487 3.49002 21.7366C3.30588 21.6173 2.37063 21.6461 2.29183 21.3326C2.27674 21.2731 2.55729 20.3468 2.58831 20.1431C2.61234 19.9855 2.5707 19.8083 2.58775 19.6437C2.60954 19.433 2.837 19.1497 2.42428 19.0091C2.16497 18.9208 1.77321 18.7753 1.52089 18.7143C1.22553 18.6431 1.11935 18.8183 1.1906 18.3461C1.21408 18.1904 1.40157 17.7389 1.38453 17.6584C1.32417 17.3748 0.345054 17.0252 0.294478 16.8455C0.238592 16.646 0.631189 16.2093 0.739886 15.9986C1.04335 15.4101 1.34066 14.7766 1.60779 14.1714C1.6824 14.0023 1.96825 13.8744 1.62735 13.7165C1.4175 13.6192 0.776771 13.5843 0.691545 13.5044C0.616659 13.434 0.783477 13.1579 0.79214 13.0559C0.870659 12.1179 0.993328 11.1977 1.09057 10.26C1.09644 10.2044 1.1811 9.83327 1.19591 9.81595C1.32249 9.66925 2.189 9.96404 2.18844 9.66114C2.18844 9.61783 1.72627 9.12688 1.69497 8.95167C1.64244 8.658 2.74115 8.13183 2.98593 7.9625C3.54619 7.57521 4.12544 7.16641 4.6815 6.76347C4.75779 6.70814 4.89806 6.52232 4.93299 6.52344C5.13781 6.52931 5.55136 7.1773 5.66621 7.38073C5.70337 7.44639 5.83722 7.66658 5.78105 7.71241V7.71269Z" fill="#ADEA4B"/>
                      <path d="M17.2575 13.1966C17.1801 13.2662 16.6564 13.3682 16.5609 13.6485C16.8286 13.865 17.3229 13.6862 17.2625 14.1492L16.0149 18.1937C15.8631 18.4046 15.7271 18.2286 15.5932 18.2099C14.573 18.0665 15.5577 18.7506 15.5555 18.8389C15.5499 19.0792 14.957 19.384 14.7667 19.5408C14.1863 20.0194 13.6137 20.5199 13.0191 20.9871C12.9182 21.0665 12.8763 21.1246 12.7243 21.0804C12.6578 21.0612 11.9777 20.1969 11.9766 20.1363C11.9727 19.9228 13.2376 19.1155 13.464 18.9364C13.6408 18.7967 14.6798 17.9662 14.7172 17.8936C14.8617 17.6125 14.9751 16.9077 15.0816 16.561C15.3238 15.773 15.57 14.964 15.7804 14.1651C15.8545 13.884 16.0775 13.599 15.9132 13.2972L12.4756 7.85733C12.4734 7.7464 13.3715 7.14255 13.5246 7.11405C13.6316 7.09421 13.6543 7.13976 13.7169 7.2085L17.2499 12.8588C17.2701 12.9306 17.2723 13.183 17.2575 13.1961V13.1966Z" fill="#ADEA4B"/>
                      <path d="M9.77591 17.6908C9.81531 17.6514 10.646 17.7196 10.8268 17.6936C10.8746 17.6869 11.2365 17.4941 11.2563 17.4746C11.2901 17.441 11.2815 17.3455 11.3231 17.2963C11.7668 16.7729 12.134 16.1791 12.5635 15.6437C12.0373 15.0894 11.6866 14.3614 11.0736 13.8976C10.8933 14.3612 11.0129 14.8597 10.9738 15.3453C10.9604 15.513 10.9045 15.8592 10.8743 16.0444C10.8615 16.1232 10.7874 16.4745 10.7721 16.4915C10.7352 16.5329 9.79379 16.5069 9.67308 16.4463C9.32352 16.2705 9.56438 16.0285 9.5764 15.8444C9.61468 15.2604 9.58562 14.846 9.67643 14.2474C9.70857 14.0351 9.60825 13.5746 9.9363 13.5039C10.0224 13.4854 10.1204 13.5916 10.2252 13.5983C10.4667 13.614 10.8878 13.6321 11.1085 13.5835C11.3734 13.5254 12.075 12.776 12.2659 13.004C12.2807 13.0216 12.2706 13.1239 12.3204 13.1959C12.419 13.3385 12.6015 13.4553 12.7177 13.6008C13.0097 13.9674 13.266 14.3788 13.5191 14.692C13.6547 14.8597 13.8802 14.9446 13.7214 15.1983C13.5345 15.4973 12.8552 15.5795 13.3333 15.9799C13.4135 16.0472 13.7681 16.2596 13.7681 16.3431C13.7681 16.3898 13.2344 16.8995 13.2171 17.0892C13.0016 17.2421 12.222 18.4576 12.1186 18.4869C11.8252 18.5702 11.4525 17.8934 11.0736 17.9901V23.9796C10.9355 23.9313 9.77591 24.0445 9.77591 23.9796V17.6908Z" fill="#ADEA4B"/>
                      <path d="M9.0833 5.21039C9.07073 5.19753 8.97656 4.89268 8.97656 4.86529C8.97712 4.76526 9.07352 4.67025 9.08051 4.56435C9.16769 3.23343 9.21575 1.88715 9.37978 0.571597C9.3957 0.44334 9.35463 0.0471102 9.3795 0.022241C9.41974 -0.0179967 10.4151 0.00296047 10.5168 0.0331387C10.9359 0.157484 10.6992 0.533315 10.6769 0.77083C10.5646 1.96678 10.5025 3.17224 10.3776 4.36484C10.3541 4.5895 10.4746 5.13494 10.2214 5.20675C10.12 5.23554 9.1227 5.25118 9.0833 5.21039Z" fill="#ADEA4B"/>
                      <path d="M6.62784 2.85051L5.35156 3.45312L7.31044 7.60185L8.58672 6.99924L6.62784 2.85051Z" fill="#ADEA4B"/>
                      <path d="M12.6654 5.81562C12.8457 5.97657 12.4106 6.45914 12.3198 6.61339C11.947 7.24797 11.593 7.95045 11.218 8.60599C10.7793 9.37302 10.5337 8.8544 9.91922 8.6113C9.80493 8.51126 9.73312 8.4266 9.78034 8.2676C9.98907 7.92949 10.2629 7.58635 10.4577 7.24629C10.7122 6.80228 10.9472 6.35715 11.2186 5.9109C11.2811 5.80807 11.2747 5.69183 11.3661 5.55938C11.7204 5.04523 11.9621 5.48897 12.3321 5.6991C12.4385 5.75945 12.6523 5.80388 12.6654 5.81562Z" fill="#ADEA4B"/>
                      <path d="M13.4638 4.90921C13.4487 4.9271 13.2905 4.91843 13.2285 4.90111C13.0818 4.77788 12.3008 4.46157 12.2723 4.35958C12.1781 4.02147 12.5685 3.79346 12.7163 3.51207L13.0684 2.71598C13.3408 2.64864 13.4819 2.86296 13.6683 2.96327C13.9248 3.10159 14.4918 3.25807 14.1674 3.66576C13.9402 3.95133 13.7231 4.37467 13.5169 4.71529C13.4688 4.79465 13.471 4.90055 13.4638 4.90921Z" fill="#ADEA4B"/>
                    </svg>
                  </template>
                </Quote>
              </div>
            </div>
            <div class="row">
              <div class="col col-12">
                <WorldSection />
              </div>
            </div>
            <div class="row">
              <div class="col col-3 col-spacer"></div>
              <div class="col col-6">
                <p>We discovered that what we were building was known as a Sneakernet, physically moving information instead of wirelessly sending it. As Aaron Z. Lewis <a href="https://www.instagram.com/p/Cw-RUuYuH1A/" target="_blank">wrote</a>, “USB Club is proof of shared presence. More meaningful than a few taps of your thumb.”</p>
              </div>
            </div>
            <div class="museum-plate">
              <div class="museum-full-width museum-photo-row museum-photo-row--sneakernet">
                <ImageGlow src="/figures/lore/squad.jpg" />
                <ImageGlow src="/figures/lore/pass-the-usb.jpg" />
                <ImageGlow src="/figures/lore/usb-earring.jpg" />
              </div>
            </div>

            <div class="row">
              <div class="col col-3 col-spacer"></div>
              <div class="col col-6">
                <p>With USBs spanning the globe and over 30,000 files transferred, we had the opportunity to create a network built around these personal memory devices.</p>
              </div>
            </div>
          </div>


          <!-- Network -->
          <div class="lore-section" id="network-section">
            <LoreSectionHeader label="Network" header="Re-defining Connection" />
            <div class="row">
              <div class="col col-3 col-spacer"></div>
              <div class="col col-6">
                <p><span class="usb-globe-illo"><GlobeUSB /></span>The physical act of plugging in your USB grounds the USB Club experience. It’s tactile, intentional, and real. While the medium was well-suited for in-person exchanges, your sneakers can only take you so far.</p>
                <p>With the release of the USB Club network, the USBs we distributed became magical. Now you could plug-in to unlock a world of creative, deliberate connections.</p>
              </div>
            </div>

            <div class="museum-plate museum-plate--network mobile-hide">
              <div class="museum-full-width">
                <div class="row">
                  <div class="col col-9 col-console">
                    <ImageGlow src="/figures/lore/console-drag-file.png" />
                  </div>
                  <div class="col col-3 col-child-absolute col-center">
                    <ImageGlow src="/figures/lore/mobile-vertical.png" />
                  </div>
                </div>
              </div>
              <div class="museum-inset museum-inset--potluck">
                <div class="museum-inset-title">Desktop and Mobile</div>
                <!-- <div class="museum-inset-subtitle"></div> -->
                <div class="museum-inset-body museum-inset-body--no-margin">Share, browse, and preserve<br>cherished files on the network.</div>
              </div>
            </div>

            <div class="museum-plate museum-plate--network mobile-show">
              <div class="museum-full-width museum-photo-row">
                <ImageGlow src="/figures/lore/console-drag-file.png" />
                <div class="museum-inset">
                  <div class="museum-inset-title">Desktop and Mobile</div>
                  <div class="museum-inset-body museum-inset-body--no-margin">Share, browse, and preserve<br>cherished files on the network.</div>
                </div>
                <ImageGlow src="/figures/lore/mobile-horizontal.png" />
              </div>
              
            </div>

            <div class="row">
              <div class="col col-3 col-spacer"></div>
              <div class="col col-6">
                <p>Like the initial potluck, the network fosters shared moments of reflection. Share one file a day to see and preserve the files your friends shared that day.</p>
                <p>As you wander through old files to share, nostalgia kicks in. You rediscover fragments of past projects, unfinished ideas, and forgotten memories. By the time you’ve chosen a file, you’ve already taken a trip down memory lane.</p>
                <p>What you share is a reflection of yourself. Over time a new identity layer emerges, shaped not by bios or status updates, but by the files themselves. This rhythm of discovery creates a level of quality and authenticity we haven’t found anywhere else online.</p>
              </div>
            </div>

            <div class="row image-row image-row--good-friction">
              <div class="col col-2 col-spacer"></div>
              <div class="col col-8 col-center">
                <GoodFriction />
              </div>
            </div>

            <div class="row">
              <div class="col col-3 col-spacer"></div>
              <div class="col col-6">
                <p>Most people don’t think about what happens to their files until it’s too late, but digital history is fragile. Files disappear, services shut down, formats become obsolete. When we pass files along, whether intentionally or not, we create small, unspoken archives.</p>
              </div>
            </div>

            <div class="row image-row image-row--archive-net">
              <div class="col col-2 col-spacer"></div>
              <div class="col col-8 col-center">
                <ArchiveNet />
              </div>
            </div>

            <div class="row">
              <div class="col col-3 col-spacer"></div>
              <div class="col col-6">
                <p>As the network expanded, the demand for faster, more reliable storage grew. Classic USBs serve many well, but power users need higher speeds and capacity. We saw an opportunity to redefine what a USB could be – not just a storage device, but a statement of intent. </p>
                <p>Transport is a transparent, dual-sided, one-terabyte solid-state drive designed for elegant performance. A future-proof solution for creators, archivists, and anyone serious about preserving their digital world.</p>
              </div>
            </div>

            <div class="museum-plate">
              <div class="museum-full-width museum-photo-row museum-photo-row--transport">
                <ImageGlow src="/figures/lore/transport-lit.png" />
                <VideoGlow src="/figures/lore/Aerodynamic-Glowbe_4.mp4" />
              </div>
              <div class="museum-inset museum-inset--potluck">
                <div class="museum-inset-title">Transport</div>
                <!-- <div class="museum-inset-subtitle"></div> -->
                <div class="museum-inset-body museum-inset-body--no-margin">A beautiful 1TB solid-state drive<br>and USB Club key. <a href="/transport" target="_blank">Learn more</a></div>
              </div>
            </div>

            <div class="row">
              <div class="col col-3 col-spacer"></div>
              <div class="col col-6">
                <p>With this new USB infrastructure in place, we're continually amazed by how it proliferates, connecting people and preserving memories in unexpected ways. In an increasingly transient digital landscape, the way you share files shapes how you remember. See you on the net.</p>
              </div>
            </div>
            <ReachOutFooter />
          </div>

          <!-- Complementary -->
          <div class="lore-section" id="complementary-section">
            <LoreSectionHeader label="Complementary" header="Research & Development" :lessBottom="true" />
            <div class="row row--no-margin">
              <div class="col col-2 col-spacer"></div>
              <div class="col col-8 col-center">
                <p style="text-align: center;">Fiction is only as good as its impact on reality. To shed light on our past concepts and explorations, we’ve collected select works from the USB Club archives. For educational purposes only.</p>
              </div>
            </div>
            <ComplementarySection />
          </div>

          <div class="ending-container ending-container--teal">
            <svg width="52" height="54" viewBox="0 0 52 54" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M23.5 49.5H23V49H23.5V49.5Z" fill="white"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M45.5 40.5L46 40.75L45.5 41V40.5Z" fill="white"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M23.25 30.9375L21.125 30.3125L21 29.6875L22.25 28.4375H22.75L23.75 27.4375L25.75 27L26 27.8125L27.125 27.4375L29.875 25.6875L30.875 25.25H32.375L33.625 24.9375L34.875 24.3125L37.25 24L37.375 24.6875L37.875 25.1875L37.625 25.4375H37.125L36.125 26.4375H35.125L34.125 27.4375H32.625L32.125 27.9375L31.625 27.4375L31.125 27.9375H30.125L29.125 28.9375H28.625L27.625 29.9375H25.75L25.25 29.4375L24.75 29.9375H24.25L23.25 30.9375Z" fill="white"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M21.875 24L19.125 24.25L17.875 23V21.5L16.875 20.5L16.375 17L15.625 16.25L15 16.125L15.125 15.25L15.5 14.375L16.125 14.25L17.625 12.75L18.875 12.5L23.375 11.0625L32.375 9L33.875 9.0625L34.875 9.5L35.625 9.75L35.875 10L35.375 10.5V12L36.375 13L35.875 13.5L36.0625 14.25L36.375 16L36.875 16.5L36.375 17L36.875 17.5V19L34.625 21.25L28.875 22.5L25.875 23.375L21.875 24ZM20.625 22.25L21.125 21.75L22.375 21.625L34.3125 18.625L34.0625 15.75L33.125 12.75L32.875 11.5L32.625 11.25L32.125 11.75H30.125L29.625 12.25L29.125 11.75L28.375 12L26.125 13.25L25.625 12.75L25.125 13.25L23.875 13.4375L21.375 14.0625L20.125 14.25L19.125 15.25H18.125L17.875 15.5L18.875 16.5V18.5L19.875 19.5L20.375 21L19.875 21.5L20.625 22.25Z" fill="white"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M22 44.25L27.25 43L27.75 43.5L28.25 43L29.5 42.8125L33.75 41.5L34.25 41L34.75 41.5L35.75 40.5H36.75L37.25 40L37.75 40.5L38.25 40L39.75 39.5L40.25 39L40.75 39.5L41.25 39L44.875 37.875L45.125 37.125L47.75 36.5L49 35.25L48.1875 32L47.8125 31L47.5 29.75L46.5 28.75L46 27.25L45 26.25L44.75 25L43.25 20.5L43 19.25L42 18.25L42.5 17.75L39.25 7L38.875 4.75L38.125 4L37.375 3.75L36.375 3.25L35.375 3.1875L31.375 4.3125L30.125 4.5L29.625 4L29.125 4.5H28.625L28.125 5L27.625 4.5L27.125 5L16.875 6.8125L14.375 7.6875L12.625 8L11.625 9L10.125 9.5L9.125 10.5L8 10.625L8.0625 11.5L15.1875 34.5L15.5 37.25L16.5 38.25V39.25L16 39.75L16.5 40.25L17 42.75L17.75 43.5L18.5 43.6875L20 44.25H22ZM7 33V32.25L9.25 30H9.75L10.25 29.5L11.25 30.5L11.5 30.25L8.94262 22.25L8.125 19.6923L6.625 15L6.375 13.25L5.875 13V13.25L5.6875 14L5.375 15.75L4.375 16.75L4 19L3.0625 21.5L3.125 22.5L3.625 23.75L4.08108 25L4.875 27.1759L7 33ZM10.875 41.875L11 40.75L12.25 39.5L13.375 39.625L13.5 40.25L14 40.5L13.8125 38.5L13.125 35L11.875 31.625L11 31.75L10.25 32L9.5 32.75V33.25L8.5 34.25V37.75L9.5 38.75L10.0625 41.875H10.875ZM16 45.25L14.875 43L14.5 40.75L14 40.5V40.75L12.75 43L12.5 43.75L12 44.25L12.5 44.75L12 45.25L11.875 45.875L11.25 46L11 46.25L11.5 46.75V48.75L13.25 50.5L13.5 50.25L13.75 49.5L14.1875 48.5L14.5 46.75L16 45.25ZM45.5 41L45 41.75L45.5 42.25L45 42.75L45.125 43.375L46.875 44.125L47 45.25L46.25 46L45.5 46.1875L42.5 47.3125L41.75 47.5L40.75 48.5L38.5 48.875L36.5 49.6875L30.25 51L29.75 51.5L29.25 51L28.75 51.5L23.5 52.25L19.5 53.25L17.75 53.5L17.25 54L16.75 53.5L16.25 54H13.25L12.25 53H10.75L10 52.25L9.375 50L8.125 43L7.125 39.5L6.5 37.75L5.5 36.75L5 33.75L0.875 23.75V23.25L0.375 22.75L0.875 22.25L1.125 20.5L3.125 14.5L3.375 13.25L4.375 12.25L4.875 9.25L5.625 8.5L7.875 8.1875L10.25 7.375L10.5 6.625L17.875 4.3125L25.375 3.125L28.875 2.25L31.125 2L31.625 1.5L32.125 2L32.625 1.5H34.625L35.625 0.5L37.625 1L38.625 2L39.75 2.125L40 2.875L40.625 3L41.375 3.75L45.375 20L45.8125 21.5L46.1875 22.5L46.5 23.75L47.5 24.75L47.625 26.375L48.375 26.625L49.5 30.25L50.5 31.25L50.75 32.5L51.25 34V35.5L51 36.75L49.25 38.5L48 38.75L45.625 39.625L45.5 40.5L45 40.75L45.25 41H45.5ZM42 43L42.125 41.625L42.75 41.5L43 41H42.75L40.25 41.5L40 42H40.25L41.375 42.375L42 43ZM38.5 42.75L39.75 42.5L40 42H39.75L38 42.3125L37.25 42.5L37 43L38.5 42.75ZM34.75 45L35.25 44.5H35.75L36.5 43.75L36.375 43.125L35.5 43.1875L32.75 44L32.5 44.5H32.75L34.75 45ZM20.5 50.75L35 47.75L42 45.1875L42.75 45L43.75 44H44.75L45 43.5H42.75L42.25 43L41.25 44L40 44.25L38.5 44.75L36.125 44.625L35.875 45.875L34.5 46.3125L32.5 47.1875L30.75 47.5L30.25 47L29.75 47.5H29.25L28.75 47L28.25 47.5L27.75 47L26.25 47.5L25.75 48L25.25 47.5L24.75 48L20.25 48.5L20 48.25L20.5 47.75L20.25 47.5H19.25L19 47.75L19.5 48.25L19.25 48.5H18.75L18.5 48.75L19 49.25V50.25L18.5 50.75L18.75 51L20.5 50.75ZM31 45V44.5H30.5V45H31ZM26.75 46L27 45.5H26.25L26 46H26.75ZM17.875 48.875L18 47.75L18.75 47H20.25L20.5 46.5L17.625 46.5625L17.1875 48L17.125 48.875H17.875Z" fill="white"/>
              <path d="M4.375 24.5L4.08108 25L4.875 27.1759L5.375 26.5L6 26L7 24L7.875 23.25L8.125 22.5L8.94262 22.25L8.125 19.6923L7.5 20.5L5.875 21.8562L4.875 22.75V23.75L4.375 24.5Z" fill="white"/>
            </svg>
            <div class="teal-cta">
              A physical, digital, and theoretical artifact<br>by
              <a href="https://tealprocess.net" target="_blank">Teal Process & Company</a>.
            </div>
          </div>

        </div>
      </div>
    </div>
    <FooterBottom :loreCta="true" />
  </div>
</template>

<script>
import WorldSection from "../Lore/WorldSection.vue"
import LoreSectionHeader from "../Lore/LoreSectionHeader.vue"
import LoreHero from "../Lore/LoreHero.vue"
import Laptop from "../Lore/illos/Laptop.vue"
import USBKeys from "../Lore/illos/USBKeys.vue"
import DJDeck from "../Lore/illos/DJDeck.vue"
import ImageGlow from "../Lore/ImageGlow.vue"
import TimePlace from "../Lore/illos/TimePlace.vue"
import MediumOfOrigin from "../Lore/illos/MediumOfOrigin.vue"
import Baggie from "../Lore/illos/Baggie.vue"
import VideoGlow from "../Lore/VideoGlow.vue"
import Quote from "../Lore/illos/Quote.vue"
import GoodFriction from "../Lore/illos/GoodFriction.vue"
import GlobeUSB from "../Lore/illos/GlobeUSB.vue" 
import ArchiveNet from "../Lore/illos/ArchiveNet.vue"
import ReachOutFooter from "../Lore/ReachOutFooter.vue"
import ComplementarySection from "../Lore/ComplementarySection.vue"
import FooterBottom from "../FooterBottom.vue"
import LoreNav from "../Lore/LoreNav.vue"
export default {
  name: "Lore",
  components: {
    // SoundBoard,
    WorldSection,
    LoreSectionHeader,
    LoreHero,
    Laptop,
    USBKeys,
    DJDeck,
    ImageGlow,
    TimePlace,
    MediumOfOrigin,
    Baggie,
    VideoGlow,
    Quote,
    GoodFriction,
    GlobeUSB,
    ArchiveNet,
    ReachOutFooter,
    ComplementarySection,
    FooterBottom,
    LoreNav
  },
  data() {
    return {
      audioFiles: [
        "/sounds/blip-01.mp3",
        "/sounds/train-blip-02.mp3",
      ],
    };
  },
  computed: {
  },
  created() {
  },
  mounted() {
  },
  methods: {
  },
  head() {
    return {
      title: "What is USB Club?",
      meta: [
        {
          tagDuplicateStrategy: "replace",
          name: "twitter:title",
          content: "What is USB Club?",
        },
        {
          tagDuplicateStrategy: "replace",
          name: "twitter:description",
          content: "Building a Memory Network",
        },
        {
          tagDuplicateStrategy: "replace",
          name: "twitter:image",
          content: "https://usb.club/figures/og/MemoryNetworkOG.png",
        },
        {
          tagDuplicateStrategy: "replace",
          name: "description",
          content: "Building a Memory Network",
        },
        {
          tagDuplicateStrategy: "replace",
          name: "image",
          content: "https://usb.club/figures/og/MemoryNetworkOG.png",
        },
        {
          tagDuplicateStrategy: "replace",
          name: "og:description",
          content: "Building a Memory Network",
        },
        {
          tagDuplicateStrategy: "replace",
          name: "og:title",
          content: "What is USB Club?",
        },
        {
          tagDuplicateStrategy: "replace",
          name: "og:image",
          content: "https://usb.club/figures/og/MemoryNetworkOG.png",
        },
      ],
    };
  },
};
</script>

<style>

/* LORE PAGE & COLUMNS */

.lore-page {
  width: 100%;
  --container-color: #232323;
  --max-width: 1344px;
}

.lore {
  width: 100%;
}

.lore-container {
  max-width: var(--max-width);
}

.lore-center {
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.lore p {
  font-size: 3.6rem;
  line-height: 1.2;
  letter-spacing: -0.05em;
  font-family: "Untitled Serif", serif;
  color: #DFDFDF;
}

.lore p + p {
  margin-top: 4.2rem;
}

.lore .row + .image-row {
  margin-top: 8rem;
}

.lore .image-row + .row {
  margin-top: 8rem;
}


/* Row with negative margin to offset column padding */
.row {
  display: flex;
  flex-wrap: wrap;
  margin: -12px;
}

.row-no-margin {
  margin: 0;
}

.row--align-bottom {
  align-items: flex-end;
}

/* Column padding equals half the gutter (12px) */
.col {
  padding: 12px;
  box-sizing: border-box;
}

.col svg {
  max-width: 100%;
}

.col-center {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.col-child-absolute {
  position: relative;
}

.col-child-absolute .glowing-image {
  width: min(270px, 25vw);
  position: absolute;
  top: 55%;
  transform: translateY(-50%);
  right: 0;
}

/* Desktop grid: 12 columns */
.col-1  { flex: 0 0 8.3333%;  max-width: 8.3333%; }
.col-2  { flex: 0 0 16.6667%; max-width: 16.6667%; }
.col-3  { flex: 0 0 25%;      max-width: 25%; }
.col-4  { flex: 0 0 33.3333%;  max-width: 33.3333%; }
.col-5  { flex: 0 0 41.6667%;  max-width: 41.6667%; }
.col-6  { flex: 0 0 50%;      max-width: 50%; }
.col-7  { flex: 0 0 58.3333%;  max-width: 58.3333%; }
.col-8  { flex: 0 0 66.6667%;  max-width: 66.6667%; }
.col-9  { flex: 0 0 75%;      max-width: 75%; }
.col-10 { flex: 0 0 83.3333%;  max-width: 83.3333%; }
.col-11 { flex: 0 0 91.6667%;  max-width: 91.6667%; }
.col-12 { flex: 0 0 100%;      max-width: 100%; }




@media screen and (max-width: 775px) {

}


/* Custom numbered list */
.numbered-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 2.4rem;
}

.numbered-list-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.numbered-list-icon {
  height: 29px;
  width: auto;
  margin-top: 0.8rem;
}

.numbered-list-icon--2 {
  margin-top: 0.6rem;
}


/* MUSEUM PLATES */

.museum-plate {
  background-color: var(--container-color);
  margin: 6.2rem 2.4rem;
  padding-top: 2.4rem;
  padding-bottom: 2.4rem;
}

.museum-plate--network {
  margin-left: 12rem;
}

.museum-plate--network .col-console {
  margin-left: -5.5vw;
}

.museum-full-width {
  width: calc(100% + 4.8rem);
  margin: 0 -2.4rem;
}

/* .museum-left-outset */

.museum-photo-row {
  height: 300px;
  gap: 2.4rem;
  display: grid;
  grid-auto-flow: column; /* Ensures images are laid out in a row */
  gap: 16px;
  align-items: flex-start;
}

.museum-photo-row .glowing-image {
  height: 300px;
}

.museum-photo-row--sneakernet,
.museum-photo-row--sneakernet .glowing-image {
  height: 310px;
}

.museum-photo-row--transport,
.museum-photo-row--transport .glowing-image,
.museum-photo-row--transport .glowing-video {
  height: 530px;
}

.museum-photo-row--transport .glowing-video {
  background: #171717;
  border-radius: 2px;
}

.museum-photo-row img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.museum-inset {
  padding: 2.4rem;
  padding-bottom: 0;
  font-size: 1.6rem;
  line-height: 130%;
  letter-spacing: 0%;
}

.museum-inset-title {
  font-weight: bold;
}

.museum-inset-subtitle {
  font-weight: 500;
}

.museum-inset-body {
  color: #C0C0C0;
  margin-top: 1.6rem;
}

.museum-inset-body--no-margin {
  margin-top: 0;
}

.museum-inset--bottom {
  align-self: flex-end;
}

.museum-video-row .row {
  align-items: flex-end;
}

.museum-video-row .museum-inset {
  padding: 1.6rem;
}


/* ILLOS */

.laptop-illo {
  bottom: -12px;
  left: -5px;
  position: relative;
}

.usbkeys-illo {
  bottom: -10px;
  left: -2px;
  position: relative;
}

p.usbkeys-paragraph {
  margin-top: -6px;
}

.djdeck-illo {
  float: left;
  position: relative;
  shape-outside: ellipse(120px 50px at 0% 0%);
  left: -96px;
  top: -50px;
}

.baggie-illo {
  float: left;
  position: relative;
  shape-outside: ellipse(60px 17px at 0% 40%);
  left: -70px;
  top: -25px;
}

.usb-globe-illo {
  float: left;
  position: relative;
  shape-outside: ellipse(115px 5px at 13% 30%);
  left: -42px;
  top: -38px;
}

.mobile-show {
  display: none;
}

.ending-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 8rem;
}

.ending-container--teal {
  margin-top: 20rem;
}

.ending-container--teal svg {
  margin-bottom: 1.2rem;
}

.teal-cta {
  text-align: center;
  font-family: "EB Garamond", serif;
  font-size: 2.4rem;
  line-height: 1.3;
}
  
.ending-cta {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2.4rem;
  color: #BEBEBE;
  font-family: "Asimov", sans-serif;
  font-size: 2.4rem;
  margin-top: 6.2rem;
}

.file-to-glowbe {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.file-to-glowbe__background {
  position: relative;
  width: 100%;
}

.file-to-glowbe__glowbes {
  position: absolute;
  width: 50%;
  height: 100%;
  right: 0;
  display: flex; 
  align-items: flex-start;
  pointer-events: none;
  z-index: 10;
}

.file-to-glowbe__glowbes .file-glowbe {
  width: 10%; /* responsive orb size */
  transform-origin: center;
  animation: glowbe-float 3s ease-in-out infinite;
}

@keyframes glowbe-float {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
  100% {
    transform: translateY(0);
  }
}

.file-to-glowbe__glowbes .file-glowbe:nth-child(1) {
  animation-delay: 0.5s;
  margin-left: 16%;
  margin-top: 34%;
  width: 22%;
}

.file-to-glowbe__glowbes .file-glowbe:nth-child(2) {
  animation-delay: 1s;
  margin-left: -5%;
  margin-top: 16%;
  width: 19%;
  opacity: 0.85;
}

.file-to-glowbe__glowbes .file-glowbe:nth-child(3) {
  animation-delay: 1.5s;
  margin-left: 6%;
  margin-top: 22%;
  width: 19%;
  opacity: 0.55;
}

.file-to-glowbe__glowbes .file-glowbe:nth-child(4) {
  animation-delay: 2s;
  margin-right: -6%;
  margin-top: 7%;
  width: 18%;
  opacity: 0.25;
}



.g1 { margin-top: 5%;  transform: scale(1);   opacity: 1;   }
.g2 { margin-top: 10%; transform: scale(0.9); opacity: 0.8; }
.g3 { margin-top: 15%; transform: scale(1.1); opacity: 0.7; }
.g4 { margin-top: 20%; transform: scale(0.8); opacity: 0.6; }

@media screen and (max-width: 1380px) {
  .row {
    margin: 0;
  }
}

/* Mobile: Stack all columns */
@media (max-width: 768px) {
  .lore-page {
    --max-width: 85vw;
  }
  .col {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .col-spacer {
    display: none;
  }

  .lore p {
    font-size: 2.4rem;
    line-height: 1.3;
    letter-spacing: -0.04em;
  }

  .lore .row + .image-row {
      margin-top: 3.2rem;
  }

  .lore .image-row + .row {
      margin-top: 1.6rem;
  }

  .museum-video-row .museum-inset {
    padding-left: 5.2rem;
  }

  .mobile-show {
    display: initial;
  }

  .mobile-hide {
    display: none;
  }

  .laptop-illo {
    bottom: -15px;
    left: -7px;
  }

  .laptop-illo svg {
    width: 95px;
  }

  .museum-photo-row {
    height: fit-content;
    grid-auto-flow: row;
  }

  .row.image-row.image-row--time-place {
    margin-top: 1.6rem !important;
  }

  .row.image-row.image-row--time-place svg {
    height: 160px;
    margin-top: -1.6rem;
  }

  .numbered-list-icon {
    height: 19px;
  }

  .museum-photo-row--transport,
  .museum-photo-row--transport .glowing-image,
  .museum-photo-row--transport .glowing-video {
    height: fit-content;
  }

  .row.image-row.image-row--good-friction svg {
    height: fit-content;
    margin-bottom: 2.4rem;
  }

  .row.image-row.image-row--archive-net svg {
    width: 70%;
    margin-bottom: 2.4rem;
  }

  .museum-plate--network.mobile-show {
    display: flex;
    margin: 0;
    margin-top: 4rem;
    margin-bottom: 4rem;
    padding-top: 1.2rem;
  }

  .museum-plate--network.mobile-show .museum-photo-row .glowing-image {
    height: fit-content;
  }

  .museum-plate--network.mobile-show .museum-inset {
    text-align: center;
    margin-top: 1.2rem;
    margin-bottom: 1.6rem;
    padding: 0;
  }

.museum-plate--network.mobile-show .museum-photo-row .glowing-image:first-of-type {
  width: 103%;
  margin-left: -1.5%;
}

.usbkeys-illo {
  float: right;
  width: 95px;
}

.usbkeys-paragraph {
  margin-top: 0;
}

.djdeck-illo {
  left: -29px;
}

.djdeck-illo svg {
  max-width: 70%;
}

#origin-section .lore-section-header {
  margin-bottom: 6rem;
}

.baggie-illo {
  shape-outside: ellipse(60px 17px at 13% 30%);
  left: -28px;
  top: -44px;
}

.baggie-illo svg {
  max-width: 80%;
}

.usb-globe-illo svg {
  width: 82%;
}

.usb-globe-illo {
  float: left;
  position: relative;
  shape-outside: ellipse(103px 20px at 10% 10%);
  left: -13px;
  top: -51px;
}
#network-section .col-section-header {
  margin-bottom: 2.4rem;
}

.ending-cta {
  flex-direction: column;
}

.ending-container--teal svg {
  width: 4.8rem;
}

.teal-cta {
  font-size: 2rem;
  line-height: 1.3;
}

#complementary-section .lore-section-header--less-bottom {
  margin-bottom: 1.6rem;
}
}
</style>
